import { FormContext } from "@common/contexts/Form";
import { FormFieldContext, type FormFieldContextProps } from "@common/contexts/FormField";
import { FormGroupContext } from "@common/contexts/FormGroup";
import { FormStyleContext } from "@common/contexts/FormStyle";
import { getTooltipAttributes } from "@common/contexts/Tooltip";
import { Alert } from "@common/controls/Alert/component";
import Hint from "@common/controls/Hint/component";
import Checkbox, { FancyCheckbox } from "@common/controls/Inputs/Checkbox/component";
import { RadioButtonGroup } from "@common/controls/Inputs/Radio";
import { type OptionItem } from "@common/controls/Inputs/Select/component";
import PlainText from "@common/controls/Inputs/Text/Plain";
import { SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { parsePadding } from "@common/lib/css";
import { usePrevious, useTrigger } from "@common/lib/hooks";
import { isNullOrUndefined, uuid } from "@common/lib/misc";
import { validateValueEx } from "@common/lib/validate";
import classnames from "classnames";
import { isFunction, isNull, isUndefined, omitBy } from "lodash";
import React, { useContext, useEffect, useRef } from "react";
import { type Validation } from "../component";
import FormFieldError from "./Error";
import { FormFieldInsideLabel, FormFieldLabel } from "./Label";
import { type Padding } from "./lib";

import "./style.less";

export type IFormFieldComponent = {
  getFinalValue?: () => Promise<any>;
};

export type SelectBehaviourFn = (options: OptionItem<any>[]) => {
  shouldHide?: boolean;
  value: any;
};
export type SelectBehaviour =
  | "preselect"
  | "preselectIfSingle"
  | "preselectAndHideIfSingle"
  | "preselectAndHide"
  | "hideIfEmpty";
export type RequiredBehaviour = "optionalIfEmpty";

export type FormFieldInlineOptions = {
  isLabelStretched: boolean;
};

export type FormComponentBaseProps = {
  className?: string;
  /** the child component can render own hint to this ref. TODO: better solution */
  hintRef?: React.RefObject<HTMLElement>;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  style?: React.CSSProperties;
};
export type FormFieldValidateOn = "blur" | "change";
export type FormFieldProps<TValue, TProps extends FormComponentBaseProps> = {
  addon?: any;
  className?: string;
  colSpan?: number | null;
  Component: React.ComponentType<TProps>;
  componentProps?: Partial<TProps>;
  disabledTooltip?: string;
  errorMessage?: string | null;
  fieldTooltip?: string;
  hint?: any | null;
  hintMode?: "alert";
  inlineOptions?: FormFieldInlineOptions;
  isDisabled?: boolean;
  isNoStyle?: boolean;
  isHidden?: boolean;
  isPaddingOnContainer?: boolean;
  isRequired?: boolean;
  isTrueRequired?: boolean;
  getFinalValue?: () => Promise<any>;
  /** Used for inside-label styles calculation only */
  label?: string | null;
  labelAddon?: any;
  link?: string /* when linked, the field will update its value when corresponding linked field changes */;
  name: string;
  onChange?: (name: string, value: TValue) => void;
  qa?: string;
  requiredBehaviour?: RequiredBehaviour;
  selectBehaviour?: SelectBehaviour | SelectBehaviourFn;
  style?: React.CSSProperties;
  tooltip?: string | null;
  validateOn?: FormFieldValidateOn;
  validator?: Validation;
  value: TValue;
};

export type FormFieldHandles = {
  validate: () => Promise<boolean>;
};

const FormField = <TValue, TProps extends FormComponentBaseProps>(props: FormFieldProps<TValue, TProps>) => {
  const i18n = useI18n();

  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<IFormFieldComponent>(null);
  const componentHintRef = useRef<HTMLDivElement>(null);
  const [isHidden, setIsHidden] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(uuid());

  const formContext = useContext(FormContext);
  const formGroupContext = useContext(FormGroupContext);
  const styleContext = useContext(FormStyleContext);

  const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(props.errorMessage);
  React.useEffect(() => setErrorMessage(props.errorMessage), [props.errorMessage]);

  const [isRequired, setIsRequired] = React.useState<boolean | undefined>(props.isRequired);
  React.useEffect(() => setIsRequired(props.isRequired), [props.isRequired]);

  const [hasContent, setHasContent] = React.useState(false);
  const [inputFocused, setInputFocused] = React.useState(false);

  const isCheckbox = React.useMemo(
    () => (props.Component as any) === Checkbox || (props.Component as any) === FancyCheckbox,
    [props.Component]
  );

  const validate = React.useCallback(async (): Promise<string | null | undefined> => {
    let vr = validateValueEx(i18n, props.value, {
      isRequired: isRequired ?? false,
      isTrueRequired: (props.isTrueRequired ?? false) || isCheckbox,
      options: props.validator
    });
    if (!(vr instanceof Promise)) vr = Promise.resolve(vr);
    const errorMessage = await vr;

    setErrorMessage(errorMessage);

    return errorMessage;
  }, [i18n, props.name, props.isTrueRequired, isCheckbox, isRequired, props.value, props.validator, setErrorMessage]);

  const triggerValidate = useTrigger(validate);

  const previousValue = usePrevious(props.value);
  React.useEffect(() => {
    if (isUndefined(previousValue) || previousValue === props.value) return;

    switch (props.validateOn) {
      case "blur": {
        !inputFocused && triggerValidate();
        break;
      }
      case "change": {
        inputFocused && triggerValidate();
      }
    }
  }, [props.value, props.validateOn, triggerValidate, previousValue, inputFocused]);

  useEffect(() => {
    if (formContext) {
      formContext.register(props.name);
    }

    return () => {
      formContext && formContext.unregister(props.name);
    };
  }, [formContext.register, formContext.unregister, props.name]);

  useEffect(() => {
    if (formGroupContext) {
      formGroupContext.register(props.name);
    }

    return () => {
      formGroupContext && formGroupContext.unregister(props.name);
    };
  }, [props.name]);

  const scrollIntoView = React.useCallback(() => {
    if (!containerRef.current) return;

    containerRef.current.scrollIntoView();
  }, [containerRef.current]);

  const isLabelStyle = React.useMemo(
    () => (props.isNoStyle ?? false) || isCheckbox || (props.Component as any) === RadioButtonGroup,
    [props.isNoStyle, isCheckbox, props.Component]
  );

  useEffect(() => {
    if (formContext) {
      formContext.patch(props.name, {
        getFinalValue: props.getFinalValue ?? fieldRef.current?.getFinalValue,
        link: props.link,
        scrollIntoView,
        setError: setErrorMessage,
        validate
      });
    }
  }, [props.getFinalValue, props.link, scrollIntoView, validate, fieldRef.current?.getFinalValue]);

  useEffect(() => {
    formGroupContext?.patch(props.name, { colSpan: props.colSpan, isHidden });
  }, [props.name, props.colSpan, isHidden]);

  const onChange = React.useCallback(
    (value: any) => {
      setErrorMessage(null);

      const onChange = props.onChange || formContext.onChange!;
      onChange(props.name, value);

      if (props.validateOn || formContext?.validateOnChange) {
        triggerValidate();
      }
    },
    [
      formContext.onChange,
      formContext.validateOnChange,
      props.name,
      props.onChange,
      props.validateOn,
      setErrorMessage,
      triggerValidate
    ]
  );

  // TODO: get rid of setTimeout
  const contextValue = React.useMemo<FormFieldContextProps>(
    () => ({
      onLoading: (isLoading: boolean) => {
        formContext.onLoading(props.name, isLoading);

        // hide if it's likely to happen. The field may be revealed when loaded, but it's better than showing and then hiding.
        if (
          isLoading &&
          props.selectBehaviour &&
          props.selectBehaviour !== "preselect" &&
          props.selectBehaviour !== "preselectIfSingle"
        ) {
          setTimeout(() => setIsHidden(true), 0);
        }
      },
      onOptionsLoaded: (options: OptionItem<any>[]) => {
        setTimeout(() => setRefreshKey(uuid()), 0);
        if (!props.selectBehaviour && !props.requiredBehaviour) return;

        const eligibleOptions = options.filter((x) => !x.isDisabled);

        const onChange = (value) => setTimeout(() => (props.onChange || formContext.onChange!)(props.name, value), 0);
        switch (props.selectBehaviour) {
          case "hideIfEmpty": {
            setIsHidden(!options.length);
            break;
          }
          case "preselect": {
            if ((isNull(props.value) || isUndefined(props.value)) && eligibleOptions.length) {
              onChange(eligibleOptions[0].value);
              setIsHidden(false);
            }
            break;
          }
          case "preselectAndHide": {
            if ((isNull(props.value) || isUndefined(props.value)) && eligibleOptions.length) {
              onChange(eligibleOptions[0].value);
              setIsHidden(true);
            }
            break;
          }
          case "preselectAndHideIfSingle": {
            if ((isNull(props.value) || isUndefined(props.value)) && eligibleOptions.length === 1) {
              onChange(eligibleOptions[0].value);
            }

            setIsHidden(options.length === 1);
            break;
          }
          case "preselectIfSingle": {
            if ((isNull(props.value) || isUndefined(props.value)) && eligibleOptions.length === 1) {
              onChange(eligibleOptions[0].value);
            }

            break;
          }
          default: {
            if (isFunction(props.selectBehaviour) && isNullOrUndefined(props.value)) {
              const { value, shouldHide } = props.selectBehaviour(options);
              onChange(value);
              setIsHidden(shouldHide ?? false);
              break;
            }

            break;
          }
        }

        switch (props.requiredBehaviour) {
          case "optionalIfEmpty": {
            if (!options.length) {
              setIsRequired(false);
            } else {
              setIsRequired(props.isRequired);
            }

            break;
          }
        }
      }
    }),
    [
      props.isRequired,
      props.name,
      props.requiredBehaviour,
      props.selectBehaviour,
      props.onChange,
      props.value,
      formContext.onChange,
      formContext.onLoading
    ]
  );

  const fieldStyle = React.useMemo(() => {
    if ((props.Component as any) === PlainText) return;

    const style = isLabelStyle ? undefined : styleContext?.fieldStyle;
    return {
      ...style,
      ...props.componentProps?.style
    };
  }, [props.Component, styleContext?.fieldStyle, isLabelStyle, props.componentProps?.style]);

  const inputLabelContainerRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const inputLabelContainer = inputLabelContainerRef.current as HTMLElement;
    const componentNode = inputLabelContainer.querySelector(".form-control");

    const content = componentNode?.textContent?.toString().trim();
    setHasContent(!!content);
  }, [refreshKey]);

  const isInsideLabelActive = React.useMemo(
    () => !!props.value || props.value === 0 || inputFocused || hasContent,
    [hasContent, inputFocused, props.value]
  );

  const labelPosition = React.useMemo(() => styleContext?.labelPosition ?? "top", [styleContext?.labelPosition]);
  const labelVisibility = React.useMemo(
    () => styleContext?.labelVisibility ?? "placeholder",
    [styleContext?.labelVisibility]
  );

  const finalFieldStyle = React.useMemo(
    () => (props.isPaddingOnContainer ? fieldStyle : omitBy(fieldStyle, (_, x) => x.startsWith("padding"))),
    [fieldStyle, props.isPaddingOnContainer]
  );

  const componentProps = React.useMemo((): Partial<TProps> | undefined => {
    if (!props.componentProps) return;
    if (!isInsideLabelActive && labelPosition === "inside" && props.componentProps.placeholder) {
      return {
        ...props.componentProps,
        placeholder: undefined
      };
    }

    return props.componentProps;
  }, [props.componentProps, isInsideLabelActive, labelPosition]);

  const formGroupField = React.useMemo(
    () => formGroupContext?.fields.find((x) => x.name === props.name),
    [formGroupContext?.fields, props.name]
  );

  const padding = React.useMemo((): Padding | undefined => {
    if (isLabelStyle) return;
    if (!styleContext?.fieldStyle) return;

    const padding = parsePadding(styleContext?.fieldStyle);
    if (
      !isLabelStyle &&
      labelPosition === "inside" &&
      labelVisibility === "small" &&
      !isInsideLabelActive &&
      props.label
    ) {
      return {
        paddingBottom: `calc(1.2em * 0.4 + ${padding?.bottom ?? 0})`,
        paddingLeft: padding?.left ?? 0,
        paddingRight: padding?.right ?? 0,
        paddingTop: `calc(1.2em * 0.4 + ${padding?.top ?? 0})`
      };
    }

    if (!padding) return;
    return {
      paddingBottom: padding.bottom ?? 0,
      paddingLeft: padding.left ?? 0,
      paddingRight: padding.right ?? 0,
      paddingTop: padding.top ?? 0
    };
  }, [
    props.label,
    styleContext?.fieldStyle,
    isLabelStyle,
    labelPosition,
    labelVisibility,
    isInsideLabelActive,
    props.componentProps?.style,
    styleContext?.fieldStyle
  ]);

  const componentStyle = React.useMemo(() => {
    if (isLabelStyle) return styleContext?.labelStyle;
    if (!styleContext?.fieldStyle) return props.componentProps?.style;
    if (!padding || props.isPaddingOnContainer) return props.componentProps?.style;

    const style: React.CSSProperties = {
      ...props.componentProps?.style,
      ...padding
    };
    if (isInsideLabelActive && labelPosition === "inside" && labelVisibility === "small") {
      style.paddingTop = 0;
    }
    return style;
  }, [
    props.isPaddingOnContainer,
    styleContext?.fieldStyle,
    labelPosition,
    labelVisibility,
    isLabelStyle,
    isInsideLabelActive,
    props.componentProps?.style,
    padding
  ]);

  const onComponentBlur = React.useCallback(() => {
    setInputFocused(false);
    props.componentProps?.onBlur && props.componentProps.onBlur();
  }, [props.componentProps?.onBlur]);
  const onComponentFocus = React.useCallback(() => {
    setInputFocused(true);
    props.componentProps?.onFocus && props.componentProps.onFocus();
  }, [props.componentProps?.onFocus]);

  return (
    <FormFieldContext.Provider value={contextValue}>
      <FormFieldContainer
        ref={containerRef}
        className={classnames("form_field", props.className, {
          hidden: (props.componentProps && props.componentProps["type"] === "hidden") || props.isHidden || isHidden,
          inline: !!props.inlineOptions,
          inline_addon: props.addon,
          label_inside: labelPosition === "inside",
          stretch_label: !!props.inlineOptions?.isLabelStretched,
          styled: !!styleContext?.fieldStyle
        })}
        colSpan={formGroupField?.colSpan ?? props.colSpan}
        style={props.style}
      >
        {!isNullOrUndefined(props.label) && labelPosition !== "inside" && (
          <FormFieldLabel
            addon={props.labelAddon}
            isRequired={isRequired && formContext?.options.requiredMode === "star"}
            label={props.label}
            name={props.name}
            style={styleContext?.labelStyle}
            tooltip={props.tooltip}
          />
        )}

        <div style={finalFieldStyle} className="content">
          <div
            ref={inputLabelContainerRef}
            className={classnames("input_label_container", `${labelVisibility}_label`, {
              inside_label_active: isInsideLabelActive
            })}
            {...getTooltipAttributes(
              props.fieldTooltip !== undefined
                ? props.fieldTooltip
                : props.isDisabled
                ? props.disabledTooltip
                : undefined
            )}
          >
            {!!props.label && labelPosition === "inside" && (
              <FormFieldInsideLabel
                addon={props.labelAddon}
                fieldStyle={styleContext?.fieldStyle}
                padding={padding}
                isRequired={isRequired && formContext?.options.requiredMode === "star"}
                label={props.label}
                labelVisibility={isInsideLabelActive ? labelVisibility : "placeholder"}
                name={props.name}
                tooltip={props.tooltip}
              />
            )}

            <props.Component
              ref={fieldRef}
              {...(componentProps as TProps)}
              className={classnames(isLabelStyle ? undefined : "form-control", props.componentProps?.className)}
              style={componentStyle}
              value={props.value}
              name={props.name}
              onChange={onChange}
              isRequired={isRequired}
              isDisabled={props.isDisabled}
              onFocus={onComponentFocus}
              onBlur={onComponentBlur}
              hintRef={componentHintRef}
              onClose={() => setInputFocused(false)}
              qa={props.qa}
            />
          </div>
          {!!props.addon && <div className="addon">{props.addon}</div>}
        </div>
        <div ref={componentHintRef} style={styleContext?.labelStyle}></div>
        {!!errorMessage && <FormFieldError error={errorMessage} qa={props.qa} name={props.name} />}
        {!!props.hint &&
          (props.hintMode === "alert" ? (
            <Alert color={SystemColor.Gray}>
              <div dangerouslySetInnerHTML={{ __html: props.hint }}></div>
            </Alert>
          ) : (
            <Hint style={styleContext?.labelStyle} hint={props.hint} />
          ))}
      </FormFieldContainer>
    </FormFieldContext.Provider>
  );
};
export default FormField;

export type FormFieldContainerProps = {
  children: any;
  className?: string;
  colSpan?: number | null;
  style?: React.CSSProperties;
};

export const FormFieldContainer = React.forwardRef(
  (props: FormFieldContainerProps, ref: React.RefObject<HTMLDivElement>) => (
    <div
      ref={ref}
      style={props.style}
      className={classnames(`field-container col-sm-${props.colSpan ?? 12}`, props.className, {
        full_width: (props.colSpan || 12) === 12
      })}
    >
      {props.children}
    </div>
  )
);

type FormFieldFakeProps = {
  colSpan?: number;
  children?: any;
  orientation?: "horizontal" | "vertical";
  label?: string;
};
export const PlaceholderFormField = (props: FormFieldFakeProps) => (
  <FormFieldContainer className={classnames("placeholder", props.orientation)} colSpan={props.colSpan}>
    {!!props.label && <FormFieldLabel label={props.label} />}

    <div className="content">{props.children}</div>
  </FormFieldContainer>
);
